import { useEffect, useState } from 'react'
import type { Frame } from '../../../shared/types'
import { ApiError, api, errorMessage } from '../../lib/api'
import { useStore } from '../../lib/store'
import { canvaApi, canvaStatusOf, type CanvaIntegrationStatus } from './api'

/** A long export fired from a modal that closes on click — the outcome lands
 *  in the store's toast, which outlives the component that started it.
 *  Frames go one at a time: Canva renders each design before answering. */
export async function sendFramesToCanva(frames: Frame[]): Promise<void> {
  const notify = useStore.getState().pushNotice
  const total = frames.length
  const failed: { frame: Frame; message: string }[] = []
  for (const [index, frame] of frames.entries()) {
    const progress = total > 1 ? ` (${index + 1} of ${total})` : ''
    notify(`Sending to Canva${progress} — this can take a minute or two…`, { busy: true })
    try {
      const result = await canvaApi.exportFrame(frame.canvasId, frame.id)
      notify(`“${result.title}” is now a Canva design`)
      if (result.editUrl) window.open(result.editUrl, '_blank')
    } catch (err) {
      if (err instanceof ApiError && err.body.error === 'canva_not_connected') {
        notify('Connect Canva on the Integrations page first')
        return
      }
      failed.push({ frame, message: errorMessage(err, 'Canva rejected the frame') })
    }
  }
  /* the toast holds one line, so a failure mid-batch is replaced by the
     next "Sending" — restate every failure once the batch is through */
  if (!failed.length) return
  const names = failed.map((f) => `“${f.frame.name}”`).join(', ')
  const reasons = [...new Set(failed.map((f) => f.message))].join('; ')
  notify(`Couldn’t send ${names} to Canva — ${reasons}`)
}

/** Null until the integrations status arrives; a failed lookup reads as
 *  "not connected" so the option stays grayed out rather than guessing. */
export function useCanvaStatus(): CanvaIntegrationStatus | null {
  const [status, setStatus] = useState<CanvaIntegrationStatus | null>(null)
  useEffect(() => {
    let live = true
    api.integrations().then(
      (all) => live && setStatus(canvaStatusOf(all)),
      () => live && setStatus({ enabled: true, connected: false }),
    )
    return () => {
      live = false
    }
  }, [])
  return status
}
